<template>
    <div class="guide-audit-step-details-container">
        <pendo-card
            v-for="(stepToDisplay, index) in stepsToDisplay"
            :key="`${index}`"
            ref="guideAuditSteps"
            data-cy="guide-audit-step"
            class="guide-audit-step"
            :title="getStepTitle(stepToDisplay)"
            body-min-height="0"
            collapsible>
            <pendo-loading-indicator :loading="loadingSteps" />
            <pendo-collapse v-if="!loadingSteps">
                <pendo-collapse-item
                    :name="`visual-changes-step-${index}`"
                    title="Visual changes"
                    type="details"
                    expanded>
                    <div
                        class="guide-audit-step-details-table"
                        :class="{ 'one-column': shouldDisplayAsOneColumn(stepToDisplay) }">
                        <div v-if="previousRevision">
                            <h2>Previous version</h2>
                            <div class="last-updated-subtitle">
                                {{ lastUpdateFormattedDateForPreviousRevision }} by
                                {{ previousRevision.lastUpdatedByUser.username }}
                            </div>
                        </div>
                        <div
                            v-if="previousRevision"
                            class="preview-cell">
                            <guide-step-preview
                                v-if="stepToDisplay.previousStep"
                                :key="`previous-${stepToDisplay.previousStep.id}`"
                                :dom="stepToDisplay.previousStep.dom"
                                :step="stepToDisplay.previousStep"
                                :guides="[getGuideForPreview(stepToDisplay.previousStep, previousRevision)]"
                                :resource-centers="[]" />
                        </div>
                        <div>
                            <h2>Selected version</h2>
                            <div class="last-updated-subtitle">
                                {{ lastUpdateFormattedDateForSelectedRevision }} by
                                {{ selectedRevision.lastUpdatedByUser.username }}
                            </div>
                        </div>
                        <div class="preview-cell">
                            <div class="preview-cell__tag">
                                <pendo-tag
                                    v-if="stepToDisplay.selectedStep && !stepToDisplay.previousStep"
                                    label="Step added"
                                    color="#009855" />
                                <pendo-tag
                                    v-if="!stepToDisplay.selectedStep"
                                    label="Step removed"
                                    color="#BD0000" />
                            </div>
                            <div :class="{ invisible: loadingSteps }">
                                <guide-step-preview
                                    v-if="stepToDisplay.selectedStep"
                                    :key="`selected-${stepToDisplay.selectedStep.id}`"
                                    :dom="stepToDisplay.selectedStep.dom"
                                    :step="stepToDisplay.selectedStep"
                                    :guides="[getGuideForPreview(stepToDisplay.selectedStep, selectedRevision)]"
                                    :resource-centers="[]" />
                            </div>
                        </div>
                    </div>
                </pendo-collapse-item>
                <pendo-collapse-item
                    v-if="stepToDisplay.hasNonVisualChanges"
                    :name="`other-changes-step-${index}`"
                    title="Other changes"
                    type="details"
                    expanded />
            </pendo-collapse>
        </pendo-card>
    </div>
</template>

<script>
import get from 'lodash/get';
import { PendoCard, PendoCollapse, PendoCollapseItem, PendoLoadingIndicator, PendoTag } from '@pendo/components';
import { BuildingBlock, BuildingBlockProperties, BuildingBlockUtils } from '@pendo/services/BuildingBlocks';
import GuideStepPreview from '@/components/guides/settings/GuideStepPreview';
import {
    formatRevisionDateTimestamp,
    stepHasNonVisualChanges,
    stepHasVisualChanges
} from '@/stateless-components/guides/audit/guide-audit-utils';
import { getBuildingBlocks } from '@/utils/guides';

export default {
    name: 'GuideAuditStepDetails',
    components: {
        GuideStepPreview,
        PendoCard,
        PendoCollapse,
        PendoCollapseItem,
        PendoLoadingIndicator,
        PendoTag
    },
    props: {
        selectedRevision: {
            type: Object,
            default: null
        },
        previousRevision: {
            type: Object,
            default: null
        },
        activeTimezone: {
            type: String,
            required: true
        }
    },
    data () {
        return {
            stepsToDisplay: [],
            loadingSteps: false
        };
    },
    computed: {
        lastUpdateFormattedDateForPreviousRevision () {
            return formatRevisionDateTimestamp(this.previousRevision.lastUpdatedAt, this.activeTimezone);
        },
        lastUpdateFormattedDateForSelectedRevision () {
            return formatRevisionDateTimestamp(this.selectedRevision.lastUpdatedAt, this.activeTimezone);
        }
    },
    watch: {
        async selectedRevision () {
            await this.loadAndDisplaySteps();
        }
    },
    async mounted () {
        await this.loadAndDisplaySteps();
    },
    methods: {
        async loadAndDisplaySteps () {
            this.loadingSteps = true;
            await this.populateStepsToDisplay();
            this.$nextTick(() => {
                this.collapseStepsWithoutChanges();
                this.loadingSteps = false;
            });
        },
        async populateStepsToDisplay () {
            if (!this.previousRevision) {
                this.stepsToDisplay = await Promise.all(
                    this.selectedRevision.steps.map(async (step, index) => {
                        const { buildingBlocks, dom } = await getBuildingBlocks(step);

                        return {
                            selectedStep: { ...step, buildingBlocks, dom },
                            previousStep: null,
                            indexToDisplay: index + 1,
                            hasVisualChanges: true,
                            hasNonVisualChanges: true
                        };
                    })
                );

                return;
            }

            const selectedStepsMap = this.selectedRevision.steps.reduce((map, step) => {
                map[step.id] = step;

                return map;
            }, {});
            const previousStepsMap = this.previousRevision.steps.reduce((map, step) => {
                map[step.id] = step;

                return map;
            }, {});

            const stepsToDisplay = [];
            let selectedIndex = 0;
            let previousIndex = 0;
            while (
                selectedIndex < this.selectedRevision.steps.length ||
                previousIndex < this.previousRevision.steps.length
            ) {
                const selectedStep =
                    selectedIndex < this.selectedRevision.steps.length
                        ? this.selectedRevision.steps[selectedIndex]
                        : null;
                const previousStepAtIndex =
                    previousIndex < this.previousRevision.steps.length
                        ? this.previousRevision.steps[previousIndex]
                        : null;
                const stepToDisplay = {
                    selectedStep: null,
                    previousStep: null,
                    indexToDisplay: selectedIndex + 1,
                    hasVisualChanges: stepHasVisualChanges(selectedStep, previousStepsMap[selectedStep.id]),
                    hasNonVisualChanges: stepHasNonVisualChanges(selectedStep, previousStepsMap[selectedStep.id])
                };

                if (!previousStepAtIndex || selectedStepsMap[previousStepAtIndex.id]) {
                    stepToDisplay.selectedStep = selectedStep;
                    stepToDisplay.previousStep = previousStepsMap[selectedStep.id];
                    selectedIndex++;
                    previousIndex++;
                } else {
                    stepToDisplay.previousStep = previousStepAtIndex;
                    stepToDisplay.indexToDisplay = null;
                    stepToDisplay.hasVisualChanges = false;
                    stepToDisplay.hasNonVisualChanges = false;
                    previousIndex++;
                }

                stepsToDisplay.push(stepToDisplay);
            }

            this.stepsToDisplay = await Promise.all(
                stepsToDisplay.map(async (stepToDisplay) => {
                    if (stepToDisplay.selectedStep) {
                        const { buildingBlocks, dom } = await getBuildingBlocks(stepToDisplay.selectedStep);
                        stepToDisplay.selectedStep = { ...stepToDisplay.selectedStep, buildingBlocks, dom };
                    }
                    if (stepToDisplay.previousStep) {
                        const { buildingBlocks, dom } = await getBuildingBlocks(stepToDisplay.previousStep);
                        stepToDisplay.previousStep = { ...stepToDisplay.previousStep, buildingBlocks, dom };
                    }

                    return stepToDisplay;
                })
            );
        },
        collapseStepsWithoutChanges () {
            this.stepsToDisplay.forEach(({ hasVisualChanges, hasNonVisualChanges }, index) => {
                if (hasVisualChanges || hasNonVisualChanges) {
                    this.$refs.guideAuditSteps[index].expandCard();
                } else {
                    this.$refs.guideAuditSteps[index].collapseCard();
                }
            });
        },
        getStepTitle ({ indexToDisplay, hasVisualChanges, hasNonVisualChanges }) {
            if (!indexToDisplay) {
                return 'Removed';
            }

            return `Step ${indexToDisplay}${hasVisualChanges || hasNonVisualChanges ? '' : ' (No changes)'}`;
        },
        getGuideForPreview (step, guide) {
            // Steps with guide data text blocks (like date blocks) require the active guide match their step.guideId for proper render
            return {
                ...guide,
                id: step.guideId
            };
        },
        shouldDisplayAsOneColumn ({ previousStep, selectedStep }) {
            return !this.previousRevision || this.isBannerStep(previousStep) || this.isBannerStep(selectedStep);
        },
        isBannerStep (step) {
            if (!step) return false;

            const buildingBlocks = JSON.parse(step.buildingBlocks);
            const guideBlock = BuildingBlock.findBlockByDomId(buildingBlocks, 'pendo-g-');
            const guideBlockLayoutProperties = BuildingBlockProperties.getGuideBlockLayoutProperties(guideBlock);
            const stepWidth = get(guideBlockLayoutProperties, 'width.value');
            if (!stepWidth) {
                return true;
            }
            if (stepWidth.includes('%')) {
                if (parseInt(stepWidth) >= 50) {
                    return true;
                }

                const stepMinWidth = get(guideBlockLayoutProperties, 'minWidth.value');
                if (stepMinWidth && parseInt(BuildingBlockUtils.dpToPx(stepMinWidth)) > 700) {
                    return true;
                }
            } else if (parseInt(BuildingBlockUtils.dpToPx(stepWidth)) > 700) {
                return true;
            }

            return false;
        }
    }
};
</script>

<style lang="scss" scoped>
.guide-audit-step-details-container {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .guide-audit-step-details-table {
        display: grid;
        grid-auto-flow: column;
        grid-template-rows: auto auto;
        padding: 16px;

        &.one-column {
            grid-auto-flow: row;
            grid-template-columns: auto;
        }

        h2 {
            margin: 0px;
            font-size: 16px;
            font-weight: 600;
        }

        .last-updated-subtitle {
            color: $color-gray-80;
            font-weight: 100;
            font-size: 12px;
            margin-bottom: 16px;
        }

        > div {
            padding: 0px 64px;
        }

        .preview-cell {
            display: flex;
            flex-direction: column;
            justify-content: center;

            &__tag {
                text-align: center;
                margin-bottom: 16px;
            }
        }
    }
}
</style>
